import { addDays } from 'date-fns'

import { DurationRange, makeDurationRange } from '@lyrahealth-inc/shared-app-logic/src/features/calendar/provider/utils'
import {
  Appointment,
  type PatientUpcomingAppointment,
} from '@lyrahealth-inc/shared-app-logic/src/models/appointment/Appointment'
import { AvailabilitiesResponse } from '@lyrahealth-inc/shared-app-logic/src/models/calendar/CalendarV2'
import { VIDEO_PLATFORM } from '@lyrahealth-inc/shared-app-logic/src/models/modality/Modality'
import { MEETING_FORMATS } from '@lyrahealth-inc/shared-app-logic/src/models/session/Session'

import { getLTAppointmentsForPatient } from './appointmentsSlice'
import { AxiosInstance, baseApi, RTKQueryTag } from '../../../../data/baseApi'

export type GetAppointmentsArgs = {
  providerId: string
  episodes?: 'current'
  view?: string
  durationRange?: DurationRange
  dateRange?: { start: string; end: string }
}

type PutAppointmentArgs = {
  providerId: string
  data: Partial<Appointment>
  showAlert?: boolean
}

type PostAppointmentArgs = {
  providerId: string
  data: Partial<Appointment>
}

type PostRecurringAppointmentArgs = {
  providerId: string
  data: {
    sessionType?: string | null
    appointment: Partial<Appointment>
    intervalDays: number
    sessionCount: number
  }
}

type PutAppointmentsBulkArgs = {
  providerId: string
  data: Partial<Appointment>[]
}

export function toBulkUpdatePayload(
  appointments: Appointment[],
  ids: number[],
  overrides: { meetingFormat: MEETING_FORMATS; address?: string; videoPlatform?: VIDEO_PLATFORM },
): Partial<Appointment>[] {
  const appointmentMap = new Map(appointments.map((a) => [a.appointmentId, a]))
  return ids.flatMap((id) => {
    const appt = appointmentMap.get(id)
    if (!appt) return []
    return [
      {
        ...appt,
        meetingFormat: overrides.meetingFormat,
        ...(overrides.address !== undefined && { address: overrides.address }),
        ...(overrides.videoPlatform !== undefined && { videoPlatform: overrides.videoPlatform }),
      },
    ]
  })
}

type BulkUpdateResponse = {
  scheduled: number[]
  skipped: number
  deduplicated: number
}

type DeleteAppointmentArgs = {
  providerId: string
  appointmentId: number
  changeReason?: string
}

type GetAppointmentsForPatientArgs = {
  providerId: string
  patientId: string
  episodeId?: string
  skipReduxDispatch?: boolean
}

type GetAppointmentByIdArgs = {
  providerId: string
  appointmentId: string | number
}

type GetProviderAvailabilityArgs = {
  providerId: string
  daysToFetch: number
  params: {
    slice_duration_minutes: number
    daily_start_time: string
    daily_end_time: string
    clientele: string
    treatment: string
    offering: string
    partner: string
    appointment_class: 'initial' | 'recurring'
    lead_time_hours: number
    appointment_duration_minutes?: number
  }
}

type ChargeReversalArgs = {
  providerId: string
  appointmentId: number
}

const appointmentsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAppointments: builder.query<Appointment[], GetAppointmentsArgs>({
      query: ({ providerId, episodes, view, durationRange, dateRange }) => {
        let range: [string, string] | undefined
        if (durationRange) {
          range = makeDurationRange(durationRange)
        }
        if (dateRange) {
          range = [dateRange.start, dateRange.end]
        }
        return {
          method: 'get',
          url: `/v1/${providerId}/appointments`,
          params: { episodes, view, startDateTime: range?.[0], endDateTime: range?.[1] },
        }
      },
      providesTags: [RTKQueryTag.APPOINTMENT, RTKQueryTag.PERSIST],
    }),
    updateAppointment: builder.mutation<Appointment, PutAppointmentArgs>({
      query: ({ providerId, data: { sessionType: _sessionType, ...data }, showAlert }) => ({
        method: 'put',
        url: `/v1/${providerId}/appointments`,
        data,
        showAlert,
      }),
      invalidatesTags: [RTKQueryTag.APPOINTMENT, RTKQueryTag.PATIENT_UPCOMING_APPOINTMENTS],
    }),
    updateAppointmentsBulk: builder.mutation<BulkUpdateResponse, PutAppointmentsBulkArgs>({
      query: ({ providerId, data }) => ({
        method: 'put',
        url: `/v1/${providerId}/appointments/bulk-update`,
        data: data.map(({ sessionType: _sessionType, ...appointment }) => appointment),
      }),
      invalidatesTags: [RTKQueryTag.APPOINTMENT, RTKQueryTag.PATIENT_UPCOMING_APPOINTMENTS],
    }),
    createAppointment: builder.mutation<Appointment, PostAppointmentArgs>({
      query: ({ providerId, data }) => ({
        method: 'post',
        url: `/v1/${providerId}/appointments`,
        data,
      }),
      invalidatesTags: [
        RTKQueryTag.APPOINTMENT,
        RTKQueryTag.NEW_CLIENTS_LIST,
        RTKQueryTag.PATIENT_UPCOMING_APPOINTMENTS,
      ],
    }),
    createRecurringAppointment: builder.mutation<void, PostRecurringAppointmentArgs>({
      query: ({ providerId, data }) => ({
        method: 'post',
        url: `/v1/${providerId}/appointments-recurring`,
        data,
      }),
      invalidatesTags: [
        RTKQueryTag.APPOINTMENT,
        RTKQueryTag.NEW_CLIENTS_LIST,
        RTKQueryTag.PATIENT_UPCOMING_APPOINTMENTS,
      ],
    }),
    cancelAppointment: builder.mutation<void, DeleteAppointmentArgs>({
      query: ({ providerId, appointmentId, changeReason }) => ({
        method: 'delete',
        url: `/v1/${providerId}/appointments/${appointmentId}`,
        params: { changeReason },
      }),
      invalidatesTags: [RTKQueryTag.APPOINTMENT, RTKQueryTag.PATIENT_UPCOMING_APPOINTMENTS],
    }),
    getAppointmentsForPatient: builder.query<Appointment[], GetAppointmentsForPatientArgs>({
      query: ({ providerId, patientId, episodeId }) => ({
        method: 'get',
        url: `/v1/${providerId}/${patientId}/appointments`,
        params: { episodeId },
      }),
      providesTags: [RTKQueryTag.APPOINTMENT],
      async onQueryStarted({ skipReduxDispatch }, { dispatch, queryFulfilled }) {
        try {
          if (!skipReduxDispatch) {
            const { data } = await queryFulfilled
            dispatch(getLTAppointmentsForPatient(data))
          }
        } catch {}
      },
    }),
    getPatientUpcomingAppointments: builder.query<PatientUpcomingAppointment[], { patientId: string }>({
      query: ({ patientId }) => ({
        method: 'get',
        url: `/lt/v1/patient/${patientId}/appointments`,
        params: { timing: 'upcoming' },
      }),
      providesTags: [RTKQueryTag.PATIENT_UPCOMING_APPOINTMENTS],
    }),
    getAppointmentById: builder.query<Appointment, GetAppointmentByIdArgs>({
      query: ({ providerId, appointmentId }) => ({
        method: 'get',
        url: `/v1/${providerId}/appointments/${appointmentId}`,
      }),
      providesTags: [RTKQueryTag.APPOINTMENT],
    }),
    getProviderAvailability: builder.query<AvailabilitiesResponse, GetProviderAvailabilityArgs>({
      query: ({ providerId, params, daysToFetch }) => ({
        method: 'post',
        url: `/api/v1/provider/availability`,
        data: { provider_ids: [providerId] },
        params: {
          start_datetime: new Date().toISOString(),
          end_datetime: addDays(new Date(), daysToFetch).toISOString(),
          ...params,
        },
        axiosInstanceType: AxiosInstance.CALENDAR,
      }),
    }),
    chargeReversal: builder.mutation<Appointment, ChargeReversalArgs>({
      query: ({ providerId, appointmentId }) => ({
        method: 'post',
        url: `/v1/providers/${providerId}/appointment/charge-reversal/${appointmentId}`,
      }),
    }),
  }),
})

export const {
  useGetAppointmentsQuery,
  useLazyGetAppointmentsQuery,
  useUpdateAppointmentMutation,
  useUpdateAppointmentsBulkMutation,
  useCreateAppointmentMutation,
  useCreateRecurringAppointmentMutation,
  useCancelAppointmentMutation,
  useGetAppointmentsForPatientQuery,
  useGetPatientUpcomingAppointmentsQuery,
  useGetAppointmentByIdQuery,
  useGetProviderAvailabilityQuery,
  useLazyGetAppointmentsForPatientQuery,
} = appointmentsApi
export default appointmentsApi
